import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCpu, FiShield, FiZap, FiCode, FiGrid, FiArrowRight, FiPlay, FiTerminal, FiDatabase, FiLock } = FiIcons;

const Landing = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="bg-brand-ivory overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="inline-flex items-center space-x-2 bg-brand-indigo/5 border border-brand-indigo/10 px-4 py-2 rounded-full mb-8">
              <span className="w-2 h-2 bg-brand-mint rounded-full animate-pulse"></span>
              <span className="text-xs font-bold text-brand-indigo uppercase tracking-widest">v1.2.0 Venice Update Live</span>
            </motion.div>
            <motion.h1 variants={itemVariants} className="text-6xl lg:text-8xl font-display font-bold text-brand-indigo leading-[1] mb-8">
              The Clinical <br /><span className="italic text-brand-slate">NLP IDE.</span>
            </motion.h1>
            <motion.p variants={itemVariants} className="text-xl text-brand-slate max-w-xl mb-12 leading-relaxed">
              HDAI Software is the world's first unified development environment for health AI. Bridging the gap from zero-code visual building to high-performance CLI automation.
            </motion.p>
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/signup" className="w-full sm:w-auto bg-brand-indigo text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-brand-slate transition-all shadow-xl hover:shadow-2xl flex items-center justify-center space-x-3 group">
                <span>Start Building</span>
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/book-demo" className="w-full sm:w-auto bg-white text-brand-indigo border border-gray-100 px-10 py-5 rounded-2xl font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center space-x-3">
                <SafeIcon icon={FiPlay} />
                <span>Watch Demo</span>
              </Link>
            </motion.div>
            
            <motion.div variants={itemVariants} className="mt-16 flex items-center space-x-8">
              <div className="flex -space-x-4">
                {[1,2,3,4].map(i => (
                  <img key={i} src={`https://i.pravatar.cc/100?img=${i+10}`} className="w-10 h-10 rounded-full border-2 border-white" alt="user" />
                ))}
              </div>
              <div className="text-sm font-medium text-brand-indigo/60">
                <span className="font-bold text-brand-indigo">200+</span> Clinical teams building on HDAI
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="relative"
          >
            <div className="bg-brand-indigo rounded-[3rem] p-4 shadow-[0_50px_100px_-20px_rgba(46,48,92,0.3)] relative z-10 overflow-hidden transform hover:scale-[1.02] transition-transform duration-700">
              <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] p-1 border border-white/10">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bbbda5366991?auto=format&fit=crop&q=80&w=1000" 
                  alt="HDAI Builder" 
                  className="rounded-[2.4rem] w-full shadow-inner opacity-90"
                />
              </div>
              {/* Animated UI overlays */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-12 left-12 bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-xl flex items-center space-x-3"
              >
                <div className="w-10 h-10 bg-brand-mint rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiZap} className="text-brand-indigo" />
                </div>
                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Status</div>
                  <div className="text-xs font-bold text-brand-indigo">Optimizing Workflow...</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature Grids */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { 
                title: "Agentic IDE", 
                desc: "Describe your clinical logic in plain English. Our agent handles the boilerplate and model orchestration.",
                icon: FiCpu 
              },
              { 
                title: "HIPAA Compliant", 
                desc: "Every inference is logged in an immutable audit trail. E2E encryption for all patient record vaults.",
                icon: FiShield 
              },
              { 
                title: "Multi-Model Native", 
                desc: "Switch between Venice-Clinical, Hugging Face, or local Llama models with a single block toggle.",
                icon: FiDatabase 
              }
            ].map((feature, i) => (
              <motion.div 
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-10 rounded-[3rem] bg-brand-ivory hover:bg-brand-indigo hover:text-white transition-all duration-500 group"
              >
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-sm group-hover:bg-brand-mint group-hover:scale-110 transition-all">
                  <SafeIcon icon={feature.icon} className="text-brand-indigo text-2xl" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-4">{feature.title}</h3>
                <p className="opacity-60 leading-relaxed font-medium">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Compiler Section */}
      <section className="py-32 bg-brand-indigo text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-brand-mint opacity-[0.03] rounded-full -mr-[400px] -mt-[400px]" />
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-20">
          <div className="lg:w-1/2">
            <h2 className="text-5xl font-display font-bold mb-8 leading-tight">Compile Clinical Logic <br /><span className="text-brand-mint italic">to Production.</span></h2>
            <div className="space-y-6 mb-12">
              {[
                { title: ".hd Compiler", desc: "Turn visual blocks into verifiable clinical bytecode." },
                { title: "Venice AI Brains", desc: "Access the world's most performant clinical reasoning models." },
                { title: "Vault Storage", desc: "E2E encrypted records ready for HIPAA audits." }
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center shrink-0 border border-white/10">
                    <SafeIcon icon={FiZap} className="text-brand-mint" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">{item.title}</h4>
                    <p className="text-white/50">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/signup" className="inline-flex items-center space-x-3 bg-brand-mint text-brand-indigo px-8 py-4 rounded-2xl font-bold hover:scale-105 transition-transform">
              <span>Try the Compiler</span>
              <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
          <div className="lg:w-1/2">
            <div className="bg-white/5 backdrop-blur-xl rounded-[3rem] p-8 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-4">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Compiler Output</div>
              </div>
              <div className="font-mono text-sm space-y-2">
                <div className="text-brand-mint">$ hd compile ./cardiology-v2.hd</div>
                <div className="text-white/60">Checking clinical constraints... [OK]</div>
                <div className="text-white/60">Verifying HIPAA BAA compatibility... [OK]</div>
                <div className="text-white/60">Orchestrating Venice-Clinical v4... [OK]</div>
                <div className="text-brand-mint pt-4 font-bold">Build Successful. Deployment Live at clinical.hd.ai/v2</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;